/* -*- coding: utf-8 -*-
 *
 * This file is part of HTU App Theme.
 * Copyright (C) 2024-2026 eco IT GmbH.
 *
 * HTU App Theme can not be copied and/or distributed without the express
 * permission of eco IT GmbH.
 */

// eslint-disable-next-line no-unused-vars
import "bootstrap/dist/js/bootstrap.js";
import { createIcons, icons } from "lucide";

import "bootstrap/js/dist/alert";
import Dropdown from "bootstrap/js/dist/dropdown";
// eslint-disable-next-line no-unused-vars
import moment from "moment";
// eslint-disable-next-line no-unused-vars
import select2 from "select2";
import GLightbox from "glightbox";
import Swiper from "swiper";
import PureCounter from "@srexi/purecounterjs";
import AOS from "aos";
import Waypoint from "waypoints/src/waypoint";

window.select2 = select2;

window.moment = moment;


(function () {
  "use strict";

  /**
   * Easy selector helper function
   */
  const select = (el, all = false) => {
    el = el.trim();
    if (all) {
      return [...document.querySelectorAll(el)];
    } else {
      return document.querySelector(el);
    }
  };

  /**
   * Easy event listener function
   */
  const on = (type, el, listener, all = false) => {
    let selectEl = select(el, all);
    if (selectEl) {
      if (all) {
        selectEl.forEach((e) => e.addEventListener(type, listener));
      } else {
        selectEl.addEventListener(type, listener);
      }
    }
  };

  /**
   * Easy on scroll event listener
   */
  const onscroll = (el, listener) => {
    el.addEventListener("scroll", listener);
  };

  /**
   * Scrolls to an element with header offset
   */
  const scrollto = (el) => {
    let header = select("#header");
    let offset = header.offsetHeight;

    let elementPos = select(el).offsetTop;
    window.scrollTo({
      top: elementPos - offset,
      behavior: "smooth",
    });
  };

  /**
   * Toggle .header-scrolled class to #header when page is scrolled
   */
  let selectHeader = select("#header");
  if (selectHeader) {
    const headerScrolled = () => {
      if (window.scrollY > 100) {
        selectHeader.classList.add("header-scrolled");
      } else {
        selectHeader.classList.remove("header-scrolled");
      }
    };
    window.addEventListener("load", headerScrolled);
    onscroll(document, headerScrolled);
  }

  /**
   * Back to top button
   */
  let backtotop = select(".back-to-top");
  if (backtotop) {
    const toggleBacktotop = () => {
      if (window.scrollY > 100) {
        backtotop.classList.add("active");
      } else {
        backtotop.classList.remove("active");
      }
    };
    window.addEventListener("load", toggleBacktotop);
    onscroll(document, toggleBacktotop);
  }

  /**
   * Mobile nav toggle
   */
  on("click", ".mobile-nav-toggle", function (e) {
    select("#navbar").classList.toggle("navbar-mobile");
    this.classList.toggle("bi-list");
    this.classList.toggle("bi-x");
  });

  /**
   * Mobile nav dropdowns activate
   */
  on(
    "click",
    ".navbar .dropdown > a",
    function (e) {
      if (select("#navbar").classList.contains("navbar-mobile")) {
        e.preventDefault();
        this.nextElementSibling.classList.toggle("dropdown-active");
      }
    },
    true
  );

  /**
   * Desktop header dropdowns open on hover; mobile keeps Bootstrap click/tap.
   *
   * Mouse clicks must not leave :focus-visible on the trigger — hovering a
   * normal nav link does not move focus, so the ring would otherwise stick
   * until another dropdown is focused.
   */
  const initDesktopHoverDropdowns = () => {
    const desktopQuery = window.matchMedia("(min-width: 992px)");
    const header = select(".site-header");
    if (!header) {
      return;
    }

    const dropdowns = select(".site-header .dropdown", true);
    if (!dropdowns.length) {
      return;
    }

    const instanceFor = (toggle) => Dropdown.getOrCreateInstance(toggle);

    const closeDropdown = (dropdown) => {
      const toggle = dropdown.querySelector('[data-bs-toggle="dropdown"]');
      if (!toggle) {
        return;
      }
      instanceFor(toggle).hide();
      if (document.activeElement === toggle) {
        toggle.blur();
      }
    };

    const closeAllDropdowns = () => {
      dropdowns.forEach(closeDropdown);
    };

    const openDropdown = (dropdown) => {
      if (!desktopQuery.matches) {
        return;
      }
      dropdowns.forEach((other) => {
        if (other !== dropdown) {
          closeDropdown(other);
        }
      });
      const toggle = dropdown.querySelector('[data-bs-toggle="dropdown"]');
      if (toggle) {
        instanceFor(toggle).show();
      }
    };

    dropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector('[data-bs-toggle="dropdown"]');
      if (!toggle || toggle.dataset.hoverDropdownBound) {
        return;
      }
      toggle.dataset.hoverDropdownBound = "1";

      dropdown.addEventListener("mouseenter", () => openDropdown(dropdown));
      dropdown.addEventListener("mouseleave", () => {
        if (desktopQuery.matches) {
          closeDropdown(dropdown);
        }
      });

      toggle.addEventListener("pointerdown", (event) => {
        if (desktopQuery.matches && event.pointerType === "mouse") {
          event.preventDefault();
        }
      });

      toggle.addEventListener("click", (event) => {
        if (!desktopQuery.matches) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        openDropdown(dropdown);
      });
    });

    header.addEventListener("mouseover", (event) => {
      if (!desktopQuery.matches) {
        return;
      }
      if (!event.target.closest(".dropdown")) {
        closeAllDropdowns();
      }
    });

    header.addEventListener("mouseleave", () => {
      if (desktopQuery.matches) {
        closeAllDropdowns();
      }
    });

    desktopQuery.addEventListener("change", closeAllDropdowns);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDesktopHoverDropdowns);
  } else {
    initDesktopHoverDropdowns();
  }

  /**
   * Scroll with offset on links with a class name .scrollto
   */
  on(
    "click",
    ".scrollto",
    function (e) {
      if (select(this.hash)) {
        e.preventDefault();

        let navbar = select("#navbar");
        if (navbar.classList.contains("navbar-mobile")) {
          navbar.classList.remove("navbar-mobile");
          let navbarToggle = select(".mobile-nav-toggle");
          navbarToggle.classList.toggle("bi-list");
          navbarToggle.classList.toggle("bi-x");
        }
        scrollto(this.hash);
      }
    },
    true
  );

  /**
   * Scroll with offset on page load with hash links in the url
   */
  window.addEventListener("load", () => {
    if (window.location.hash) {
      if (select(window.location.hash)) {
        scrollto(window.location.hash);
      }
    }
  });

  /**
   * Initiate lightbox
   */
  const glightbox = GLightbox({
    selector: ".glightbox",
  });

  /**
   * Skills animation
   */
  let skilsContent = select(".skills-content");
  if (skilsContent) {
    new Waypoint({
      element: skilsContent,
      offset: "80%",
      handler: function (direction) {
        let progress = select(".progress .progress-bar", true);
        progress.forEach((el) => {
          el.style.width = el.getAttribute("aria-valuenow") + "%";
        });
      },
    });
  }

  /**
   * Clients Slider
   */
  new Swiper(".clients-slider", {
    speed: 400,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    slidesPerView: "auto",
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
    breakpoints: {
      320: {
        slidesPerView: 2,
        spaceBetween: 40,
      },
      480: {
        slidesPerView: 3,
        spaceBetween: 60,
      },
      640: {
        slidesPerView: 4,
        spaceBetween: 80,
      },
      992: {
        slidesPerView: 6,
        spaceBetween: 120,
      },
    },
  });

  /**
   * Init swiper slider with 1 slide at once in desktop view
   */
  new Swiper(".slides-1", {
    speed: 600,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    slidesPerView: "auto",
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
  });

  /**
   * Initiate portfolio lightbox
   */
  const portfolioLightbox = GLightbox({
    selector: ".portfolio-lightbox",
  });

  /**
   * Portfolio details slider
   */
  new Swiper(".portfolio-details-slider", {
    speed: 400,
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    pagination: {
      el: ".swiper-pagination",
      type: "bullets",
      clickable: true,
    },
  });

  /**
   * Animation on scroll
   */
  window.addEventListener("load", () => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      mirror: false,
    });
  });

  /**
   * Initiate Pure Counter
   */
  new PureCounter();

  /**
   * Lucide icons – replace <i data-lucide="name"> with inline SVGs
   */
  createIcons({ icons });
})();
